import { useEffect, useLayoutEffect, useMemo } from 'react'

import { DateHelper, Select } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Field, Layout } from '@loadsmart/miranda-react'

import { type Feature } from 'core/domain/Feature'
import { type MarketTag } from 'core/domain/MarketTag'
import { type Mode } from 'core/domain/Mode'
import { filterOptions, mapToOptions, type Option } from 'core/domain/Option'
import { getStopFromLocation, type Stop } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { mapStringToStop } from 'core/infra/StopMapper'
import { formatFeatureLabel } from 'core/presenters/featurePresenter'
import { formatMarketTagLabel } from 'core/presenters/marketTagPresenter'
import { formatStopToOption } from 'core/presenters/stopPresenter'
import { DateSelect } from 'core/ui/components/date/DateSelect'
import { FilterSummary } from 'core/ui/components/filter-summary/FilterSummary'
import { Icon } from 'core/ui/components/icon/Icon'
import { LaneSelect } from 'core/ui/components/lane/LaneSelect'
import { ModeSelect } from 'core/ui/components/mode/ModeSelect'
import { RadiusSelect } from 'core/ui/components/radius/RadiusSelect'
import { TruckSelect } from 'core/ui/components/truck/TruckSelect'
import { useSearchState } from 'core/ui/hooks/useSearchState'
import { toArray } from 'core/utils/array'

import analytics from 'legacy/utils/analytics'

import { type PreferredLane } from 'preferred-lanes/domain/PreferredLane'
import { PreferredLaneToggle } from 'preferred-lanes/ui/PreferredLaneToggle'

import {
  DEFAULT_FEATURES,
  DEFAULT_MARKET_TAGS,
  DEFAULT_MODES,
  DEFAULT_RADIUS_VALUE,
  DEFAULT_TRUCK_TYPES,
  getNewLaneState,
} from '../../domain/filter/LoadboardFilter'
import { formatFilterSummaryItems } from '../../presenters/filterPresenter'
import { useTrackLaneSearch } from '../hooks/useTrackLaneSearch'
import {
  GridWrapper,
  PreferredLanesWrapper,
  StyledField,
  StyledToggle,
} from './LoadboardFilters.styles'

/**
 * List of possible load types
 */
const LOAD_TYPE_OPTIONS = mapToOptions({
  values: DEFAULT_MARKET_TAGS,
  getLabel: value => formatMarketTagLabel(value) ?? '',
  getValue: value => value,
  sort: false,
})

/**
 * List of possible load types
 */
const LOAD_FEATURES_OPTIONS = mapToOptions({
  values: DEFAULT_FEATURES,
  getLabel: value => formatFeatureLabel(value) ?? '',
  getValue: value => value,
  sort: false,
})

function getCoordinates(coordinates?: string) {
  const [lat, lng] = coordinates?.split(',') ?? []

  return {
    latitude: Number(lat),
    longitude: Number(lng),
  }
}

function createPreferredLane({
  pickup,
  pickupCoordinates,
  delivery,
  deliveryCoordinates,
  equipments,
}: FilterState): PreferredLane | undefined {
  if (!pickupCoordinates && !deliveryCoordinates) return

  return {
    origin: pickup
      ? {
          description: pickup,
          ...getCoordinates(pickupCoordinates),
        }
      : undefined,
    destination: delivery
      ? {
          description: delivery,
          ...getCoordinates(deliveryCoordinates),
        }
      : undefined,
    equipmentTypes: equipments,
  }
}

type SelectType = EventLike<Selectable | Selectable[] | null>

export type LoadboardFiltersProps = {
  onFilterReady: () => void
}

export type FilterState = {
  pickup?: string
  pickupCoordinates?: string
  delivery?: string
  deliveryCoordinates?: string
  radius?: number
  filterByPreferredLanes?: boolean
  pickupDateRangeStart?: string
  pickupDateRangeEnd?: string
  modes?: Array<Mode>
  equipments?: Array<TruckType>
  type?: Array<MarketTag>
  features?: Array<Feature>
}

export function LoadboardFilters({ onFilterReady }: Readonly<LoadboardFiltersProps>) {
  const [state, setState] = useSearchState<FilterState>()

  const {
    pickup,
    pickupCoordinates,
    delivery,
    deliveryCoordinates,
    radius,
    filterByPreferredLanes,
    pickupDateRangeStart,
    pickupDateRangeEnd,
    modes,
    equipments,
    type,
    features,
  } = state

  const { trackLaneSearch } = useTrackLaneSearch()

  const preferredLane = useMemo(
    () =>
      createPreferredLane({
        pickup,
        pickupCoordinates,
        delivery,
        deliveryCoordinates,
        equipments,
      }),
    [delivery, deliveryCoordinates, equipments, pickup, pickupCoordinates]
  )

  useEffect(() => {
    trackLaneSearch({ ...preferredLane, radius })
  }, [preferredLane, radius, trackLaneSearch])

  function handleChangeLane(newValue: { pickup?: Stop; delivery?: Stop }) {
    const { pickup, delivery } = newValue

    const newLaneState = getNewLaneState({
      pickup,
      delivery,
      previousPuCoordinates: pickupCoordinates,
      previousDelCoordinates: deliveryCoordinates,
    })

    setState(prevState => ({
      ...prevState,
      ...newLaneState,
    }))

    analytics.track('Load Board / Filters / Auto Search Lane')
  }

  function handleChangeRadius(newValue?: number) {
    if (pickup || delivery) {
      setState(prevState => ({
        ...prevState,
        radius: newValue,
      }))
    }
  }

  function handleChangePreferredLane() {
    setState(prevState => ({
      ...prevState,
      filterByPreferredLanes: filterByPreferredLanes ? undefined : true,
    }))
  }

  function handleDatesChange(newValue: { startDate?: string; endDate?: string }) {
    const { startDate, endDate } = newValue

    setState(prevState => ({
      ...prevState,
      pickupDateRangeStart: startDate,
      pickupDateRangeEnd: endDate,
    }))
  }

  function handleModeChange(newValue: Array<Mode> = []) {
    setState(prevState => ({
      ...prevState,
      modes: newValue,
    }))
  }

  function handleEquipmentChange(newValue: Array<TruckType> = []) {
    setState(prevState => ({
      ...prevState,
      equipments: newValue,
    }))
  }

  function handleTypeChange({ target: { value } }: SelectType) {
    const newValue = toArray(value).map(option => (option as Option).value as MarketTag)

    setState(prevState => ({
      ...prevState,
      type: newValue,
    }))
  }

  function handleFeaturesChange({ target: { value } }: SelectType) {
    const newValue = toArray(value).map(option => (option as Option).value as Feature)

    setState(prevState => ({
      ...prevState,
      features: newValue,
    }))
  }

  const pickupStop = useMemo(() => mapStringToStop(pickup), [pickup])
  const deliveryStop = useMemo(() => mapStringToStop(delivery), [delivery])
  const typeValue = useMemo(() => filterOptions(LOAD_TYPE_OPTIONS, type), [type])
  const featuresValue = useMemo(() => filterOptions(LOAD_FEATURES_OPTIONS, features), [features])
  const filterSummaryItems = useMemo(() => formatFilterSummaryItems(state), [state])

  function handleClearFilterItem(id: string) {
    if (id === 'lane') {
      setState(prevState => ({
        ...prevState,
        pickup: undefined,
        pickupCoordinates: undefined,
        delivery: undefined,
        deliveryCoordinates: undefined,
        filterByPreferredLanes: undefined,
        radius: undefined,
      }))
      return
    }

    if (id === 'period') {
      setState(prevState => ({
        ...prevState,
        pickupDateRangeStart: undefined,
        pickupDateRangeEnd: undefined,
      }))
      return
    }

    setState(prevState => ({
      ...prevState,
      [id]: undefined,
    }))
  }

  function handleClearFilter() {
    setState({})
  }

  useLayoutEffect(onFilterReady, [onFilterReady])

  return (
    <Layout.Stack gap="spacing-6">
      <GridWrapper>
        <LaneSelect
          id="lane"
          pickupLabel="Pickup"
          deliveryLabel="Delivery"
          style={{ gridArea: 'lane' }}
          pickupValue={pickupStop}
          deliveryValue={deliveryStop}
          mapLocationToValue={getStopFromLocation}
          mapValueToOption={formatStopToOption}
          onChange={handleChangeLane}
          disabled={filterByPreferredLanes}
        />
        <RadiusSelect
          id="radius"
          name="radius"
          label="Miles"
          placeholder="Select"
          style={{ gridArea: 'radius' }}
          multiple={false}
          disabled={filterByPreferredLanes}
          value={radius ?? DEFAULT_RADIUS_VALUE}
          onChange={handleChangeRadius}
        />
        <PreferredLanesWrapper id="preferred-lane">
          <StyledToggle
            value="filter-by-preferred-lane"
            leading={<Icon name="markers-favorite" />}
            checked={filterByPreferredLanes}
            role="switch"
            onClick={handleChangePreferredLane}
          >
            Filter by preferred lanes
          </StyledToggle>
        </PreferredLanesWrapper>
        <DateSelect
          id="date"
          name="date"
          label="Date"
          dateFormat="yyyy-MM-dd"
          style={{ gridArea: 'date' }}
          startDateValue={pickupDateRangeStart}
          endDateValue={pickupDateRangeEnd}
          // @ts-expect-error
          constraints={[[null, DateHelper().subtract('day', 1)]]}
          onChange={handleDatesChange}
        />
        <ModeSelect
          label="Mode"
          placeholder="Modes"
          name="mode"
          style={{ gridArea: 'mode' }}
          modes={DEFAULT_MODES}
          multiple
          value={modes}
          onChange={handleModeChange}
        />
        <TruckSelect
          id="equipment"
          label="Equipment"
          name="equipment"
          placeholder="Equipment"
          value={equipments}
          style={{ gridArea: 'equipment' }}
          truckTypes={DEFAULT_TRUCK_TYPES}
          multiple
          onChange={handleEquipmentChange}
        />
        <StyledField style={{ gridArea: 'type' }}>
          <Field.Label id="label-type">Type</Field.Label>
          <Select
            id="type"
            name="type"
            placeholder="Type"
            options={LOAD_TYPE_OPTIONS}
            multiple
            value={typeValue}
            onChange={handleTypeChange}
          />
        </StyledField>
        <StyledField style={{ gridArea: 'feature' }}>
          <Field.Label id="label-feature">Feature</Field.Label>
          <Select
            id="feature"
            name="feature"
            placeholder="Feature"
            options={LOAD_FEATURES_OPTIONS}
            multiple
            value={featuresValue}
            onChange={handleFeaturesChange}
          />
        </StyledField>
      </GridWrapper>
      <FilterSummary
        items={filterSummaryItems}
        onClearAll={handleClearFilter}
        onClearItem={handleClearFilterItem}
        extraActions={
          preferredLane && (
            <PreferredLaneToggle
              analyticsPrefix="Loadboard Filter"
              preferredLane={preferredLane}
              compact
            />
          )
        }
      />
    </Layout.Stack>
  )
}
